import React, { useState, useMemo } from 'react';
import { Icon } from '@iq/react-components';
import {
  useFloating,
  useHover,
  useInteractions,
  offset,
  autoUpdate,
  flip,
  shift,
  safePolygon,
} from '@floating-ui/react';
import SelectWidget from '../widgets/SelectWidget';
import { CustomDescription } from '../utils/CustomDescription';
import './CustomUnitLabelField.scss';

function getEnumOptions(schema) {
  if (Array.isArray(schema.anyOf)) {
    return {
      options: schema.anyOf.map((option) => ({
        value: option.const,
        label: option.title,
      })),
      selectSchema: {
        ...schema,
        enum: schema.anyOf.map((option) => option.const),
        enumNames: schema.anyOf.map((option) => option.title),
      },
    };
  }
  if (Array.isArray(schema.oneOf)) {
    return {
      options: schema.oneOf.map((option) => ({
        value: option.enum[0],
        label: option.title,
      })),
      selectSchema: {
        ...schema,
        enum: schema.oneOf.map((option) => option.enum[0]),
        enumNames: schema.oneOf.map((option) => option.title),
      },
    };
  }
  return { options: [], selectSchema: schema };
}

export default function CustomUnitLabelField(properties) {
  const { required, onChange, schema, formData, name, formContext } = properties;
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const { options: enumOptions, selectSchema } = useMemo(() => getEnumOptions(schema), [schema]);

  const hasNoOptions = useMemo(() => {
    return enumOptions.length === 0;
  }, [enumOptions]);

  const renderedDescription = useMemo(() => {
    return CustomDescription(schema.description, formContext);
  }, [schema.description, formContext]);
  const { x, y, strategy, refs, context } = useFloating({
    open: isTooltipVisible,
    placement: schema.position || 'right',
    middleware: [offset(15), flip(), shift()],
    onOpenChange: setIsTooltipVisible,
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    handleClose: schema.isInteractive ? safePolygon() : undefined,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const showTooltip =
    (name === 'unit' && formData === 'variable-default') ||
    name === 'unitLabel' ||
    schema.showTooltip;

  return (
    <div className="form-group field field-string custom-unit-label-field">
      <label className="control-label">
        {schema.title}
        {showTooltip && (
          <span className="tooltip-icon-wrapper">
            <div
              className="tooltip-container"
              ref={refs.setReference}
              {...getReferenceProps()}
            >
              <Icon
                icon="he-info"
                size="s"
                className="input-tooltip-description"
              />
              {isTooltipVisible && (
                <div
                  className="tooltip"
                  ref={refs.setFloating}
                  style={{
                    position: strategy,
                    top: y ?? 0,
                    left: x ?? 0,
                    maxWidth: schema.tooltipFullWidth ? '100%' : '30rem',
                    pointerEvents: schema.isInteractive ? 'auto' : 'none',
                  }}
                  {...getFloatingProps()}
                >
                  {renderedDescription}
                </div>
              )}
            </div>
          </span>
        )}
      </label>

      {/* Renders dropdown for unit using anyOf or oneOf, else text input for unitLabel */}
      {name === 'unit' || schema.oneOf || schema.anyOf ? (
        <SelectWidget
          schema={selectSchema}
          formContext={formContext}
          id={name}
          options={{ enumOptions: hasNoOptions ? [] : enumOptions }}
          value={formData}
          required={required}
          placeholder={selectSchema.placeholder}
          onChange={onChange}
          readonly={schema.readOnly}
          disabled={schema.disabled}
        />
      ) : (
        <input
          type="text"
          className="avt-react-components--input"
          value={formData}
          required={required}
          placeholder={schema.placeholder}
          onChange={(e) => onChange(e.target.value)}
          disabled={schema.disabled}
          readOnly={schema.readOnly}
        />
      )}
    </div>
  );
}
